import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import PmAnalyticsFilterBar, { employeeLookup } from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, {
  EMPTY_FILTERS, latestPeriod, serializeFilters,
} from '../../components/performance/pmAnalyticsFilters';
import {
  TAB_SUMMARY, TAB_TASKS, activeTaskFilterKeys, clearTaskFilter, parseProcessHealth, serializeProcessHealth,
} from '../../components/performance/processHealthLinks';
import { anyStale, fetchPeriods, oldestCalculatedAt } from '../performance-operations/config';
import SummaryTab from './tab-pages/SummaryTab';
import TasksTab from './tab-pages/TasksTab';
import { taskChipLabel } from './config/tasks';
import {
  Header, PageContainer, PageEmpty, TabContent,
} from './styles';

// PM Analitik Faz 2 Plan 4 (spec §7) — E02 Süreç Takibi (/performance/process-health). Yalnız İK (rota + menü requiresEnterpriseAdmin,
// PM aboneliği + koltuk). Üstte ortak süzgeç şeridi (İK Operasyon Panosu ile aynı), sekmeler Özet | Görevler. Ortak süzgeçler,
// seçili sekme, M03 kartının aşaması ve Görevler süzgeçleri URL'dedir (yenileme ve geri dönüşte korunur, §19/1).
export default function PerformanceProcessHealthPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { filters, setFilters } = usePmAnalyticsFilters();
  const ph = useMemo(() => parseProcessHealth(location.search), [location.search]);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [assigneeName, setAssigneeName] = useState('');
  const [height, setHeight] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // Dönem zorunlu ve temizlenemez: URL'de yoksa (ya da artık yoksa) en yeni dönem seçilir.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  // Görevler sekmesinde tablo yüksekliği sekme içeriğinden ölçülür (tek kaydırma: yalnız tablo satırları).
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => setHeight(entry.contentRect.height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // E02'ye özgü durum: ortak süzgeç anahtarlarına dokunmadan yazılır.
  const setPh = useCallback((patch) => {
    const current = parseProcessHealth(location.search);
    history.replace({ pathname: location.pathname, search: serializeProcessHealth({ ...current, ...patch }, location.search) });
  }, [history, location.pathname, location.search]);

  // "Sıfırla" rolün varsayılanına döner: dönem ve sekme korunur; ortak süzgeçler, görev süzgeçleri ve M03 aşaması kalkar.
  const reset = useCallback(() => {
    history.replace({
      pathname: location.pathname,
      search: serializeProcessHealth({ tab: ph.tab }, serializeFilters({ ...EMPTY_FILTERS, periodId: filters.periodId })),
    });
  }, [history, location.pathname, ph.tab, filters.periodId]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Sorumlu süzgecinin etiketinde ad görünür (kimlikten çözülür).
  const assigneeId = ph.taskFilters.assigneeEmployeeId;
  useEffect(() => {
    if (!assigneeId) { setAssigneeName(''); return undefined; }
    let alive = true;
    employeeLookup.onGetById(assigneeId).then((r) => { if (alive) setAssigneeName(r?.name || ''); });
    return () => { alive = false; };
  }, [assigneeId]);

  const isTasks = ph.tab === TAB_TASKS;
  const extraChips = isTasks
    ? activeTaskFilterKeys(ph.taskFilters).map((key) => ({
      key,
      label: taskChipLabel(t, i18n.language, key, ph.taskFilters, assigneeName),
      onRemove: () => setPh({ taskFilters: clearTaskFilter(ph.taskFilters, key) }),
    }))
    : [];
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; yukarıdaki etki en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const tabs = [
    { key: TAB_SUMMARY, label: t('performance.processHealth.tab.summary') },
    { key: TAB_TASKS, label: t('performance.processHealth.tab.tasks') },
  ];

  return (
    <PageContainer>
      <Header>
        <Breadcrumbs items={[{ label: t('performance.processHealth.title') }]} />
        {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
        {periods.length > 0 ? (
          <PmAnalyticsFilterBar
            periods={periods}
            filters={filters}
            onChange={setFilters}
            onReset={reset}
            lastUpdated={isTasks ? null : oldestCalculatedAt(metas)}
            stale={isTasks ? false : anyStale(metas)}
            groupMembership={filters.groupIds.length ? 'current' : null}
            extraChips={extraChips}
          />
        ) : null}
        {ready ? <Tabs tabs={tabs} activeTab={ph.tab} onChange={(tab) => setPh({ tab })} /> : null}
      </Header>
      <TabContent ref={contentRef} $scroll={!isTasks} data-tab={ph.tab}>
        {ready && !isTasks ? (
          <SummaryTab
            key={filters.periodId}
            filters={filters}
            m03Stage={ph.m03Stage}
            onM03StageChange={(stage) => setPh({ m03Stage: stage })}
            onMeta={onMeta}
            refreshKey={refreshKey}
            onChanged={onChanged}
          />
        ) : null}
        {ready && isTasks ? (
          <TasksTab
            filters={filters}
            taskFilters={ph.taskFilters}
            onTaskFiltersChange={(next) => setPh({ taskFilters: next })}
            height={height}
          />
        ) : null}
      </TabContent>
    </PageContainer>
  );
}
